import type { CompanyShellRow } from "../../generated/core-api";
import type {
  CompanyShellCsvColumn,
  CompanyShellCsvParseError,
} from "../types";
import { formatCompanyShellRowForPreview } from "./parseCompanyShellCsv";

export interface CompanyShellLiveRowIssue {
  category: "required" | "duplicate_company_code";
  rowIndex: number;
  csvLineNumber: number;
  column: string;
  message: string;
}

export function isRequiredCompanyShellParseError(
  error: CompanyShellCsvParseError
): boolean {
  return (
    !!error.column &&
    error.message === `"${error.column}" is required.`
  );
}

export function isDuplicateCompanyCodeParseError(
  error: CompanyShellCsvParseError
): boolean {
  return (
    error.column === "Company Code" &&
    error.message.startsWith('Duplicate "Company Code"')
  );
}

/**
 * Recompute row issues whose truth can change while the user edits Review.
 * Both Review highlighting and Import Notes consume this result.
 */
export function collectCompanyShellLiveRowIssues(
  rows: CompanyShellRow[],
  columns: CompanyShellCsvColumn[],
  rowCsvLineNumbers: number[]
): CompanyShellLiveRowIssue[] {
  const issues: CompanyShellLiveRowIssue[] = [];

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const display = formatCompanyShellRowForPreview(rows[rowIndex]);
    const csvLineNumber = rowCsvLineNumbers[rowIndex] ?? rowIndex + 1;
    for (const column of columns) {
      if (
        column.requiredness !== "required" ||
        (display[column.header] ?? "").trim() !== ""
      ) {
        continue;
      }
      issues.push({
        category: "required",
        rowIndex,
        csvLineNumber,
        column: column.header,
        message: `"${column.header}" is required.`,
      });
    }
  }

  const firstCompanyCode = new Map<string, number>();
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const code = rows[rowIndex].company_code.trim();
    if (!code) continue;

    const key = code.toLowerCase();
    const firstCsvLineNumber = firstCompanyCode.get(key);
    if (firstCsvLineNumber === undefined) {
      firstCompanyCode.set(
        key,
        rowCsvLineNumbers[rowIndex] ?? rowIndex + 1
      );
      continue;
    }

    issues.push({
      category: "duplicate_company_code",
      rowIndex,
      csvLineNumber: rowCsvLineNumbers[rowIndex] ?? rowIndex + 1,
      column: "Company Code",
      message: `Duplicate "Company Code" "${code}" — already used on row ${firstCsvLineNumber}.`,
    });
  }

  return issues;
}
